import { useState } from "react";
import { toast } from "sonner";
import { Facebook, Linkedin, Link2, Check, MessageCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PetitionCTA } from "@/components/PetitionCTA";
import { track } from "@/lib/analytics";

const SHARE_TEXT = "Rust langs de A1 — 1 minuut langer rijden. Iedere dag meer rust. Samen voor 80 km/u.";

export const FinalCTA = () => {
  const [copied, setCopied] = useState(false);
  const pageUrl = typeof window !== "undefined" ? window.location.origin : "";

  const shareLinks = [
    {
      channel: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${pageUrl}`)}`,
    },
    {
      channel: "facebook",
      label: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
    },
    {
      channel: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`,
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      toast.success("Link gekopieerd");
      track("share_click", { channel: "copy_link" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopiëren is niet gelukt");
    }
  };

  return (
    <section data-testid="final-cta-section" className="relative overflow-hidden bg-gooi-green py-28 text-gooi-offwhite md:py-40">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 left-0 hidden h-96 w-96 -translate-x-1/3 rounded-full bg-gooi-forest/60 blur-3xl md:block"
      />
      <div className="relative mx-auto max-w-[90rem] px-5 md:px-12">
        <Reveal>
          <h2 className="max-w-4xl font-display text-4xl font-medium leading-[1.05] tracking-tighter sm:text-6xl lg:text-7xl">
            1 minuut langer rijden.
            <br />
            Iedere dag meer rust.
          </h2>
          <p className="mt-7 max-w-xl text-base leading-relaxed text-gooi-offwhite/75 md:text-lg">
            Help ons de minister te laten zien dat er draagvlak is voor 80 km/u op dit deel van de A1.
          </p>
          <div className="mt-10">
            <PetitionCTA location="bottom" tone="light" size="xl" />
            <p className="mt-4 text-sm font-medium text-gooi-offwhite/60">Iedere handtekening telt.</p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-16 border-t border-gooi-offwhite/15 pt-8">
            <p className="text-xs uppercase tracking-[0.25em] text-gooi-offwhite/50">Deel deze actie</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {shareLinks.map((s) => (
                <a
                  key={s.channel}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`share-${s.channel}-button`}
                  onClick={() => track("share_click", { channel: s.channel })}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-gooi-offwhite/25 px-5 text-sm font-semibold text-gooi-offwhite/85 transition-colors duration-300 hover:bg-gooi-offwhite hover:text-gooi-green"
                >
                  <s.icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                  {s.label}
                </a>
              ))}
              <button
                type="button"
                data-testid="share-copy-button"
                onClick={copyLink}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-gooi-offwhite/25 px-5 text-sm font-semibold text-gooi-offwhite/85 transition-colors duration-300 hover:bg-gooi-offwhite hover:text-gooi-green"
              >
                {copied ? (
                  <Check className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                ) : (
                  <Link2 className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                )}
                {copied ? "Gekopieerd" : "Kopieer link"}
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
